import React, { useRef, useState } from 'react';
import { useIntl } from 'umi';
import classnames from 'classnames';
import { validateUploadFilesWithErrorNotice } from '@gp/utils/file';

import type { IMimeTypeGetter } from '@home/utils/file';

import '@gp/components/DragableUploader/index.scss';

import FileUploadImage from '@gp/assets/svg/upload.svg';

// 开启批量上传时，并发上传文件数量限制
const MAX = 6;

// 允许上传的文件格式
const ACCEPT_MIMETYPES = [
  // mp4格式
  'video/mp4',
  // mov格式
  'video/quicktime',
];

interface IUploaderProps {
  multiple?: boolean;
  max?: number;
  accept?: string[];
  // 文件大小上限，默认没有上限
  limitSize?: number;
  getMimeType?: IMimeTypeGetter;
  onSelect: (files: File[], e: React.ChangeEvent<HTMLElement> | React.DragEvent<HTMLInputElement>) => void;
  // 格式校验失败时，也会触发这个回调，回传本次上传的文件列表（包含校验通过、校验不通过的）
  onValidateError?: (files: File[], e: React.ChangeEvent<HTMLElement> | React.DragEvent<HTMLInputElement>) => void;
  // 上传框是否处于拖拽状态中变化的回调
  onDraggingChange?: (payload: boolean) => void;
  footer?: React.ReactElement;
  placeholder?: React.ReactElement;
  placeholderWhenDragging?: React.ReactElement;
  className?: string;
  style?: React.CSSProperties;
}

const transferFiles2FileArray = (files: FileList | null): File[] => {
  if (!files) return [];
  return Array.prototype.slice.call(files, 0);
};

const UploaderPlaceholder = () => {
  const { formatMessage } = useIntl();

  return (
    <div className="dba-uploader__placeholder">
      <img src={FileUploadImage} className="dba-uploader__placeholder__icon" alt="" />
      <div className="dba-uploader__placeholder__info-list">
        <div className="dba-uploader__placeholder__info-item">
          {formatMessage({ id: 'dbg_motion_capture_uploader_placeholder_text1' })}
        </div>
        <div className="dba-uploader__placeholder__info-item">
          {formatMessage({ id: 'dbg_motion_capture_uploader_placeholder_text2' })}
        </div>
      </div>
    </div>
  );
};

const UploaderPlaceholderWhenDragging = () => {
  const { formatMessage } = useIntl();

  return (
    <div className="dba-uploader__placeholder">
      <img src={FileUploadImage} className="dba-uploader__placeholder__icon" alt="" />
      <div className="dba-uploader__placeholder__info-list">
        <div className="dba-uploader__placeholder__info-item">
          {formatMessage({ id: 'dbg_motion_capture_release_the_mouse' })}
        </div>
      </div>
    </div>
  );
};

const UploaderFooter = () => {
  const { formatMessage } = useIntl();

  return <div className="dba-uploader__footer">{formatMessage({ id: 'dbg_motion_capture_uploader_footer_text' })}</div>;
};

const Uploader: React.FC<IUploaderProps> = (props) => {
  const {
    multiple = false,
    max = MAX,
    accept = ACCEPT_MIMETYPES,
    limitSize,
    getMimeType,
    onSelect,
    onValidateError,
    onDraggingChange,
    placeholder = <UploaderPlaceholder />,
    placeholderWhenDragging = <UploaderPlaceholderWhenDragging />,
    footer = <UploaderFooter />,
    style,
    className,
  } = props;

  const { formatMessage } = useIntl();

  const fileInput = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e: React.DragEvent<HTMLInputElement>) => {
    e?.preventDefault();
    setIsDragging(true);
    onDraggingChange?.(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLInputElement>) => {
    e?.preventDefault();
  };
  const handleDragLeave = (e: React.DragEvent<HTMLInputElement>) => {
    e?.preventDefault();
    setIsDragging(false);
    onDraggingChange?.(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e?.preventDefault();
    const originalFiles = e?.dataTransfer?.files;
    if (!originalFiles?.length) {
      setIsDragging(false);
      onDraggingChange?.(false);
      return;
    }
    const isValid = validateUploadFilesWithErrorNotice(originalFiles, formatMessage, {
      multiple,
      max,
      accept,
      limitSize,
      getMimeType,
    });
    if (!isValid) {
      onValidateError?.(transferFiles2FileArray(originalFiles), e);
      setIsDragging(false);
      onDraggingChange?.(false);
      return;
    }
    onSelect(transferFiles2FileArray(originalFiles), e);
    setIsDragging(false);
    onDraggingChange?.(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const originalFiles = e?.target?.files;
    const isValid = validateUploadFilesWithErrorNotice(originalFiles, formatMessage, {
      multiple,
      max,
      accept,
      limitSize,
      getMimeType,
    });
    if (!isValid) {
      onValidateError?.(transferFiles2FileArray(originalFiles), e);
      // REMARK: 注意这一行清空input的value的代码  一定要在回传FileList数据之后 再执行，否则会导致组件调用者获取不到FileList数据
      fileInput.current!.value = '';
      return;
    }
    onSelect(transferFiles2FileArray(e?.target?.files), e);
    fileInput.current!.value = '';
  };

  return (
    <div style={style} className={classnames('dba-uploader', className, { highlight: isDragging })}>
      <div className="dba-uploader__content">
        <input
          ref={fileInput}
          type="file"
          className="dba-uploader__input"
          multiple={multiple}
          accept={accept?.join(',')}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onChange={handleFileInputChange}
        />
        {!isDragging && React.isValidElement(placeholder) && placeholder}
        {isDragging && React.isValidElement(placeholderWhenDragging) && placeholderWhenDragging}
      </div>
      {React.isValidElement(footer) && footer}
    </div>
  );
};

export default Uploader;
